import { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import { NavLink, Link as RouterLink } from 'react-router';
import { Wordmark } from '@/components/brand/Wordmark';
import { ColorSchemeToggle } from './ColorSchemeToggle';
import { navItems } from '@/config/site';

/**
 * Site header. Inline navigation from the `md` breakpoint up, a drawer below it.
 *
 * The AppBar is transparent and un-elevated by design - the header should read as
 * part of the page rather than a floating bar over it.
 */
export function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="transparent"
      sx={{
        backdropFilter: 'blur(12px)',
        backgroundColor:
          'color-mix(in srgb, var(--mui-palette-background-default) 82%, transparent)',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Container>
        <Toolbar disableGutters sx={{ gap: 2, minHeight: { xs: 68, md: 84 } }}>
          <Box
            component={RouterLink}
            to="/"
            aria-label="Hello Kando - home"
            sx={{ display: 'inline-flex', textDecoration: 'none', mr: 'auto' }}
          >
            {/* The link is already labelled, so the mark must not name the brand again. */}
            <Wordmark size="1.6rem" title="" />
          </Box>

          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {navItems.map((item) => (
              <Button
                key={item.to}
                component={NavLink}
                to={item.to}
                color="inherit"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&.active': { color: 'text.primary', fontWeight: 600 },
                }}
              >
                {item.label}
              </Button>
            ))}
            <Button
              component={RouterLink}
              to="/contact"
              variant="contained"
              color="primary"
              // The wordmark link is also named "Hello Kando". Two links with near
              // identical names and different destinations is a maze for anyone
              // navigating by link, so this one says where it goes. The visible text
              // is the start of the accessible name, which is what WCAG 2.5.3 wants.
              aria-label="Hello Kando - get in touch"
              sx={{ ml: 1.5 }}
            >
              Hello Kando
            </Button>
          </Stack>

          {/* Outside the desktop-only Stack: the scheme control belongs on mobile too. */}
          <ColorSchemeToggle />

          <IconButton
            edge="end"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260, pt: 2 }} role="presentation">
          <List>
            {navItems.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                onClick={() => setDrawerOpen(false)}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
